import { Fragment, memo, useCallback, useMemo, useState } from 'react';
import {
  Currency,
  DivTooltip,
  StepCSSTransitionGroup,
  TooltipBody,
  TooltipHeader,
  Tabs,
  Interpolate,
} from '@wg/wows-react-uikit';

import {
  getCompetitionMetricPoints,
  getEnemyClans,
  getLeagueData,
  getOwnClan,
  getOwnPlayers,
  getOwnResult,
  getVersusTitle,
  ROUND_TYPE,
} from '~/helpers/cwars';
import { thousands } from '~/helpers/formatting';
import { t } from '~/helpers/localization';

import InGroupClanRatingTable from '~/pages/ClanWars/InGroupClanRatingTable/InGroupClanRatingTable';
import RawProgressBar, { getMetric, getProgressPercent } from '~/pages/ClanWars/ProgressBar/RawProgressBar';
import { Result } from '~/UIKit';
import { RESULTS } from '~/UIKit/Result/Result';

import styles from './Round.scss';

import HistoryRatingTable from '../HistoryRatingTable/HistoryRatingTable';
import progressBarStyles from '../ProgressBar/ProgressBar.scss';
import VersusHeader from '../VersusHeader/VersusHeader';

import type { IBreakPointRendered } from '../ProgressBar/ProgressBar';
import type { ISideData } from '../VersusHeader/VersusHeader';
import type { IInGroupClanData } from '~/pages/ClanWars/InGroupClanRatingTable/InGroupClanRatingTable';
import type { ICommonInfo, IHistoryRound, IHistoryRoundClan, IWarSettings } from '~/types/declaration';

const defaultLeague = {
  icons: { default: '' },
  number: 0,
  points: [] as number[],
  title: '',
};

interface RoundProps {
  isVisible: boolean;
  data: IHistoryRound;
  commonInfo: ICommonInfo;
  warSettings: IWarSettings;
  withMedal?: boolean;
  hideAdditionalInfo?: boolean;
}

const Round = ({ isVisible, data, commonInfo, warSettings, withMedal, hideAdditionalInfo }: RoundProps) => {
  const [resultTabIndex, setResultTabIndex] = useState<'0' | '1'>('0');

  const maxPersonalAttempts = useMemo(() => {
    return warSettings?.stages.war.maxPersonalAttempts ?? 0;
  }, [warSettings]);

  const ownClan = useMemo(() => getOwnClan(data), [data]);
  const ownResult = useMemo(() => getOwnResult(data), [data]);
  const enemyClans = useMemo(() => getEnemyClans(data), [data]);
  const roundType = data.type;

  const myClan: ISideData | null = useMemo(() => {
    if (!ownClan) {
      return null;
    }

    return {
      id: ownClan.id,
      tag: ownClan.tag,
      color: ownClan.color,
      realm: ownClan.realm,
      score: ownClan.stars,
      leagueData: getLeagueData(warSettings.leagues, commonInfo.clan.league),
    };
  }, [commonInfo.clan.league, ownClan, warSettings.leagues]);

  const enemyClansSideData: ISideData[] = useMemo(() => {
    return enemyClans.map((enemyClan) => ({
      id: enemyClan.id,
      tag: enemyClan.tag,
      color: enemyClan.color,
      realm: enemyClan.realm,
      score: enemyClan.stars,
      leagueData: defaultLeague,
    }));
  }, [enemyClans]);

  const renderProgressBar = useCallback(() => {
    const competitionMetric = data?.settings?.preparation?.competitionMetric ?? '';
    const clanProgressMilestones: IBreakPointRendered[] = data?.settings?.preparation?.clanProgressMilestones ?? [];

    const maxClanProgress = data.maxPreparationProgress;
    const clanProgress = data.preparationProgress;

    return (
      <StepCSSTransitionGroup level={1}>
        <div>
          <div>
            <span className={styles.preparationTitle}>{t('клан не выполнил задачу этапа подготовки')}</span>
          </div>

          <div className={styles.preparationProgressWrapper}>
            <RawProgressBar
              breakpoints={clanProgressMilestones}
              breakpointRender={(item, index) => {
                const rightOffsetPercent = 100 - getProgressPercent(item.value, maxClanProgress);
                const isReachedGrade = item.value <= clanProgress;
                const rewardAmount = typeof item.reward === 'number' ? item.reward : item.reward.amount;

                return (
                  <DivTooltip
                    key={`BreakPoint_${index}`}
                    className={`${progressBarStyles.breakpoint} ${
                      isReachedGrade ? progressBarStyles.breakpointReached : ''
                    }`}
                    style={{ right: `${rightOffsetPercent}%` }}
                    tooltipBody={
                      <Fragment>
                        <TooltipHeader isBold={true}>
                          {t('Этап %(stage)s', { stage: index + 1 })}
                          <span className={styles.tooltipSubTitle}>
                            <br />
                            {thousands(item.value)} {getMetric(competitionMetric)}
                          </span>
                        </TooltipHeader>

                        <TooltipBody>
                          {t('Награда за этап:')}
                          <span className={progressBarStyles.reward}>
                            &nbsp;
                            <Currency isFlat amount={rewardAmount} type="oil" isBold={false} />
                          </span>
                        </TooltipBody>
                      </Fragment>
                    }
                  >
                    <div
                      className={`${progressBarStyles.breakpointIcon} ${
                        progressBarStyles[`breakpointIcon-${typeof item.reward === 'number' ? '' : item.reward.type}`]
                      }`}
                    />
                    <div className={progressBarStyles.breakpointValue}>{`${rewardAmount}`}</div>
                  </DivTooltip>
                );
              }}
              progressTooltip={
                <Interpolate
                  className={styles.rewardDescriptionRow}
                  str={t('Прогресс задачи:  %(totalValue)s')}
                  totalValue={thousands(clanProgress)}
                  parentTag="span"
                />
              }
              total={clanProgress}
              maxValue={maxClanProgress}
              totalValue={clanProgress}
              myValue={0}
              mPoints={getCompetitionMetricPoints(competitionMetric)}
            />
          </div>
        </div>
      </StepCSSTransitionGroup>
    );
  }, [data]);

  const renderVersusHeader = useCallback(
    (result: string, left: ISideData, right?: ISideData, group?: ISideData[], title?: string) => {
      return (
        <StepCSSTransitionGroup level={1}>
          <div>
            <div>
              <span className={styles.roundTitle}>{title || getVersusTitle(result)}</span>
            </div>
            <VersusHeader left={left} right={right} group={group} withMedal={withMedal} />
          </div>
        </StepCSSTransitionGroup>
      );
    },
    [withMedal],
  );

  const renderResults = useCallback((clan: IHistoryRoundClan | undefined, result: string, title?: string) => {
    if (!clan) {
      return null;
    }

    return (
      <StepCSSTransitionGroup level={2}>
        <Result
          rating={clan.publicRating - clan.ratingDelta}
          ratingDelta={clan.ratingDelta}
          reward={clan.isRewarded ? clan.oilEarned : clan.oilEarned + clan.reward.amount}
          result={result}
          ready={clan.isRewarded}
          title={title}
        />
      </StepCSSTransitionGroup>
    );
  }, []);

  const renderRatingTable = useCallback(() => {
    return (
      <StepCSSTransitionGroup level={3}>
        <div className={styles.ratingTable}>
          <div className={styles.ratingTableHeader}>{t('Участники сражения')}</div>
          <HistoryRatingTable data={getOwnPlayers(data)} maxPersonalAttempts={maxPersonalAttempts} />
        </div>
      </StepCSSTransitionGroup>
    );
  }, [data, maxPersonalAttempts]);

  const renderGroupRatingTable = useCallback(
    (clansData: IHistoryRoundClan[]) => {
      const clanRatings: IInGroupClanData[] = clansData.map((clan) => {
        const players = [...clan.players].sort(
          (a, b) => new Date(b.lastWarBattleAt ?? 0).getTime() - new Date(a.lastWarBattleAt ?? 0).getTime(),
        );

        const lastWarBattleAt = players.length > 0 ? players[0].lastWarBattleAt : undefined;

        return {
          id: clan.id,
          name: clan.name,
          tag: clan.tag,
          color: clan.color,
          result: clan.stars,
          lastWarBattleAt,
          memberCount: clan.memberCount,
        } satisfies IInGroupClanData;
      });

      return (
        <StepCSSTransitionGroup level={3}>
          <div className={styles.ratingTable}>
            <div className={styles.ratingTableHeaderTab}>
              <Tabs
                isUppercase
                onToggleTab={(nextTabIndex: string) => {
                  setResultTabIndex(nextTabIndex as '0' | '1');
                }}
                tabSelected={resultTabIndex}
                tabs={[
                  { content: t('Участники сражения'), value: '0' },
                  { content: t('Групповые результаты'), value: '1' },
                ]}
              />
            </div>

            <StepCSSTransitionGroup level={4}>
              {resultTabIndex === '0' ? (
                <HistoryRatingTable data={getOwnPlayers(data)} maxPersonalAttempts={maxPersonalAttempts} />
              ) : (
                <InGroupClanRatingTable data={clanRatings} selfId={data.ownClanId} background={false} />
              )}
            </StepCSSTransitionGroup>
          </div>
        </StepCSSTransitionGroup>
      );
    },
    [data, maxPersonalAttempts, resultTabIndex],
  );

  if (!isVisible) {
    return null;
  }

  if (ownResult === RESULTS.FAILED_PREPARATION) {
    return (
      <div className={styles.round}>
        {renderProgressBar()}
        {renderResults(ownClan, RESULTS.DRAW, t('Получено за Подготовку'))}
      </div>
    );
  }

  if (!ownClan || !myClan) {
    return null;
  }

  if (roundType === ROUND_TYPE.PAIR) {
    return (
      <div className={styles.round}>
        {enemyClansSideData.length > 0 ? renderVersusHeader(ownResult, myClan, enemyClansSideData[0]) : null}
        {renderResults(ownClan, ownResult)}
        {!hideAdditionalInfo ? renderRatingTable() : null}
      </div>
    );
  }

  return (
    <div className={styles.round}>
      {renderVersusHeader(ownResult, myClan, undefined, enemyClansSideData, t('Групповой этап'))}
      {renderResults(ownClan, RESULTS.WIN)}
      {!hideAdditionalInfo ? renderGroupRatingTable(data.clans) : null}
    </div>
  );
};

export default memo(Round);
